import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import PropTypes from 'prop-types'

import Btn from '../Btn'
import Loading from '../Loading'
import { PlainTextarea } from '../PlainField'

import http from '../../shared/http'
import { TApi } from '../../shared/const'
import { useAvatarUrls } from '../../shared/useAvatarUrls'

import styles from './style.module.scss'
import { formatVienna } from '../../shared/datetime'

/**
 * Der Reiter „Kommentare" — überall derselbe.
 *
 * Oben das Feld zum Erfassen, darunter eine Zeitleiste aus den automatisch
 * mitgeschriebenen Ereignissen und den Kommentaren der Verwaltung. Beides in
 * **einer** Liste: Zwei getrennte Listen zwängen zum Hin- und Herspringen, um
 * zu verstehen, was wann passiert ist.
 *
 * Ein Kommentar bleibt intern. Wer etwas mitteilen will, nimmt die Nachrichten
 * bzw. beim Unternehmen den dortigen Kommentar mit Empfänger:innen.
 *
 * Datensätze, die schon einen eigenen Änderungsverlauf führen
 * (Veranstaltungen, Vorlagen, Unterstützungsbedarfe), behalten ihn: Er zeigt
 * Vorher/Nachher je Feld und damit mehr, als hier möglich wäre. Dort steht
 * dieser Block über dem bestehenden Verlauf, im selben Reiter.
 *
 * Meist ist es die Verwaltung, die hier schreibt — deshalb die Admin-Route als
 * Standard. Eine Ansicht mit anderem Zugang gibt ihre eigene Adresse als `url`
 * mit (die Junior Company zu einer Expert:in, `COMMON_SUPPORTER_LOG`); der
 * Reiter sieht überall gleich aus, nur das Ende ist ein anderes.
 */

// Was in der Zeitleiste steht, wenn ein Ereignis automatisch mitgeschrieben
// wurde. Die Schlüssel kommen aus `services/recordLog` im Backend.
export const RECORD_EVENT_LABELS = {
	SCHOOL_CREATED: 'School created',
	SCHOOL_APPROVED: 'School approved',
	SCHOOL_UPDATED: 'Master data changed',
	SCHOOL_MERGED_IN: 'School merged in',
	// Konto einer Lehrkraft dieser Schule gelöscht (#239), Name in `message`.
	SCHOOL_TEACHER_DELETED: 'Teacher account deleted',
	USER_UPDATED: 'Profile changed',
	USER_TEACHER_ACTIVATED: 'Teacher activated',
	USER_MERGED_IN: 'Account merged in',
	USER_PARENTAL_CONFIRMATION_REVIEWED: 'Parental confirmation reviewed',
	USER_PARENTAL_CONFIRMATION_REJECTED: 'Parental confirmation rejected',
	USER_COURSE_CERTIFICATE_REVIEWED: 'Course certificate reviewed',
	USER_COURSE_CERTIFICATE_REJECTED: 'Course certificate rejected',
	// Erinnerungen per E-Mail geändert — der Satz vorher → nachher steht in `message`.
	USER_REMINDERS_CHANGED: 'Reminder changes',
	INVOICE_CREATED: 'Invoice created',
	INVOICE_PAID: 'Invoice paid',
	INVOICE_BALANCE_CHANGED: 'Partial payment recorded',
	INVOICE_REMINDER_SENT: 'Payment reminder sent',
	INVOICE_CANCELLED: 'Invoice cancelled',
	// Guthaben aus einem Storno — an der neuen Rechnung angerechnet, am
	// stornierten Beleg übernommen bzw. offen (Anforderung #91).
	INVOICE_CREDIT_APPLIED: 'Invoice credit applied',
	INVOICE_CREDIT_TRANSFERRED: 'Invoice credit transferred',
	INVOICE_CREDIT_OPEN: 'Invoice credit open',
	REPORT_SUBMITTED: 'Report submitted',
	REPORT_UNSUBMITTED: 'Report returned',
	EXPERT_CREATED: 'Expert added',
	EXPERT_UPDATED: 'Details changed',
	EXPERT_CONFIRMED: 'Expert confirmed',
	EXPERT_UNCONFIRMED: 'Expert confirmation withdrawn',
	// Die Zusammenarbeit einer Junior Company mit einer Expert:in, aus der Sicht
	// der Junior Company beschriftet.
	SUPPORTER_REQUEST_SENT: 'Request sent',
	SUPPORTER_REQUEST_ACCEPTED: 'Request accepted',
	SUPPORTER_REQUEST_DECLINED: 'Request declined',
	SUPPORTER_REQUEST_AUTO_DECLINED: 'Request expired without an answer',
	SUPPORTER_ENDED_BY_COMPANY: 'Collaboration ended by the Junior Company',
	SUPPORTER_ENDED_BY_SUPPORTER: 'Collaboration ended by the expert'
}

export default function RecordComments({ entityType, entityId, url, hint, className }) {
	const { t } = useTranslation()

	const [entries, setEntries] = useState(null)
	const [message, setMessage] = useState('')
	const [isSaving, setSaving] = useState(false)

	const endpoint = url || TApi.ADMIN_RECORD_LOG(entityType, entityId)

	const load = useCallback(() => {
		if (!entityId) return Promise.resolve()

		return (
			http
				.get(endpoint)
				// Nur eine Liste einsetzen: Antwortet der Server einmal etwas
				// anderes, bleibt der Reiter leer, statt die ganze Ansicht mit
				// einem Renderfehler auszublenden.
				.then(({ data }) => setEntries(Array.isArray(data) ? data : []))
				.catch(() => setEntries([])) // Meldung kommt aus shared/http
		)
	}, [endpoint, entityId])

	useEffect(() => {
		setEntries(null)
		load()
	}, [load])

	const submit = () => {
		const text = message.trim()
		if (!text) return

		setSaving(true)
		http.post(endpoint, { message: text })
			.then(() => {
				setMessage('')
				toast.success(t('Entry saved'))
				return load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	// Ereignisse und Kommentare in einer Liste, das Neueste zuerst.
	const timeline = useMemo(
		() =>
			(entries || []).map(entry => ({
				id: entry.id,
				createdAt: entry.createdAt,
				isComment: entry.type === 'COMMENT',
				title:
					entry.type === 'COMMENT' ? t('Comment') : t(RECORD_EVENT_LABELS[entry.eventKey] || entry.eventKey),
				message: entry.message,
				authorName: entry.authorName,
				avatarHashName: entry.authorAvatarHashName || null
			})),
		[entries, t]
	)

	/*
	 * Die Bilder werden angemeldet geholt und als lokale Adresse eingesetzt —
	 * ein `<img src>` auf die Schnittstelle schickt keinen Anmelde-Kopfeintrag
	 * mit (siehe `shared/useAvatarUrls`).
	 */
	const avatarUrls = useAvatarUrls(useMemo(() => timeline.map(entry => entry.avatarHashName), [timeline]))

	// Ohne Datensatz gibt es nichts zu kommentieren — beim Anlegen steht der
	// Reiter deshalb mit einem Hinweis da statt mit einem toten Eingabefeld.
	if (!entityId) {
		return (
			<div className={clsx(styles.root, className)}>
				<p className={styles.hint}>{t('Comments are available once the record is saved')}</p>
			</div>
		)
	}

	return (
		<div className={clsx(styles.root, className)}>
			<div className={styles.form}>
				<PlainTextarea
					rows={3}
					placeholder={t('Type a comment')}
					value={message}
					onChange={e => setMessage(e.target.value)}
				/>
				<div className={styles.submitRow}>
					<span className={styles.hint}>{hint || t('Comments stay internal')}</span>
					<Btn loading={isSaving} disabled={!message.trim()} type="button" onClick={submit}>
						{t('save')}
					</Btn>
				</div>
			</div>

			{entries === null && <Loading />}
			{entries !== null && !timeline.length && <p className={styles.empty}>{t('No entries yet')}</p>}

			{!!timeline.length && (
				<ol className={styles.timeline}>
					{timeline.map(entry => (
						<li key={entry.id} className={styles.timelineItem}>
							{/*
							 * Am Kommentar das Gesicht, am Ereignis der Punkt: Einen
							 * Kommentar hat jemand geschrieben, ein Ereignis ist bloß
							 * geschehen. Wer kein Bild hinterlegt hat — oder dessen
							 * Konto es nicht mehr gibt —, bekommt seine Initialen.
							 */}
							{entry.isComment ? (
								<Avatar
									className={styles.timelineAvatar}
									src={avatarUrls[entry.avatarHashName] || undefined}
									name={entry.authorName || '?'}
									color="#e5eaf5"
									fgColor="#22404d"
									size="28"
									round
								/>
							) : (
								<span className={clsx(styles.timelineDot, styles.timelineDotEvent)} />
							)}
							<div className={styles.timelineBody}>
								<div className={styles.timelineHead}>
									<span className={styles.timelineTitle}>{entry.title}</span>
									<span className={styles.timelineMeta}>
										{entry.createdAt && formatVienna(entry.createdAt, 'dd.MM.yyyy HH:mm')}
										{entry.authorName && ` · ${entry.authorName}`}
									</span>
								</div>
								{entry.message && <p className={styles.timelineText}>{entry.message}</p>}
							</div>
						</li>
					))}
				</ol>
			)}
		</div>
	)
}

RecordComments.propTypes = {
	entityType: PropTypes.string.isRequired,
	entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	// Abweichende Adresse für Ansichten außerhalb des Admin-Bereichs.
	url: PropTypes.string,
	hint: PropTypes.string,
	className: PropTypes.string
}
